import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Box,
  Autocomplete,
  Chip,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  ListItemIcon,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  LocalShipping as TruckIcon,
  Assignment as TaskIcon,
} from '@mui/icons-material';
import { makeApiCall } from '../../config/api';
import { formatScheduledDate } from '../../utils/dateUtils';

const CreateJobDialog = ({ open, onClose, onSuccess, trucks }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Form data. Crew is NOT set here - it comes from the assigned truck's
  // current_driver_user_id / current_passenger_user_id (legacy parity).
  // selected_services is an array of top-level service-category keys that
  // the crew logs screen expects (lawn, gardening, spring, fall, trimming,
  // turf, snow). We synthesize the `available_services` JSON on submit.
  const [formData, setFormData] = useState({
    creation_method: 'manual', // 'manual' or 'from_quote'
    quote_id: '',
    customer_id: '',
    job_type: 'summer',
    scheduled_date: new Date().toISOString().split('T')[0],
    description: '',
    truck_id: '',
    selected_services: [],
  });

  // Lookup data
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const steps = [
    'Job Source',
    'Details & Scheduling',
    'Review & Create'
  ];

  // ====================================
  // DATA FETCHING
  // ====================================

  useEffect(() => {
    if (open) {
      fetchApprovedQuotes();
      fetchCustomers();
    }
  }, [open]);

  const fetchApprovedQuotes = async () => {
    try {
      const response = await makeApiCall('api/quotes?status=approved&limit=100');
      if (response.ok) {
        const data = await response.json();
        setQuotes(data.quotes || []);
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await makeApiCall('api/customers?limit=100');
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  // ====================================
  // EVENT HANDLERS
  // ====================================

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setFormData({
      creation_method: 'manual',
      quote_id: '',
      customer_id: '',
      job_type: 'summer',
      scheduled_date: new Date().toISOString().split('T')[0],
      description: '',
      truck_id: '',
      selected_services: [],
    });
    setSelectedQuote(null);
    setSelectedCustomer(null);
    setError('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      let endpoint = 'api/jobs/create-from-quote';
      let payload = { ...formData };

      if (formData.creation_method === 'manual') {
        // Manual job creation. Crew comes from the assigned truck's default
        // driver/passenger (legacy parity) - no per-job crew list.
        // Synthesize available_services from the selected service keys so the
        // crew's log screen can render checkboxes.
        const availableServices = {};
        (formData.selected_services || []).forEach((key) => {
          availableServices[key] = { enabled: true };
        });

        endpoint = 'api/jobs';
        payload = {
          customer_id: formData.customer_id,
          job_type: formData.job_type,
          scheduled_date: formData.scheduled_date,
          description: formData.description,
          truck_id: formData.truck_id || null,
          available_services: Object.keys(availableServices).length > 0
            ? availableServices
            : null,
        };
      }

      const response = await makeApiCall(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        onSuccess(data.message || 'Job created successfully');
        handleClose();
      } else {
        setError(data.error || 'Failed to create job');
      }
    } catch (error) {
      console.error('Error creating job:', error);
      setError('Failed to create job');
    } finally {
      setLoading(false);
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 0: // Job Source
        if (formData.creation_method === 'from_quote') {
          return formData.quote_id !== '';
        } else {
          return formData.customer_id !== '';
        }
      case 1: // Details & Scheduling
        return formData.job_type !== '' && formData.scheduled_date !== '';
      case 2: // Review
        return true;
      default:
        return true;
    }
  };

  // ====================================
  // FORM FIELD HANDLERS
  // ====================================

  const handleQuoteSelection = (quote) => {
    setSelectedQuote(quote);
    setFormData(prev => ({
      ...prev,
      quote_id: quote.id,
      customer_id: quote.customer_id,
      description: `Job for Quote #${quote.quote_number}`,
      job_type: determineJobTypeFromQuote(quote)
    }));
  };

  const handleCustomerSelection = (customer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id,
      description: `Manual job for ${customer.company_name || `${customer.first_name} ${customer.last_name}`}`
    }));
  };

  const determineJobTypeFromQuote = (quote) => {
    // Determine job type based on quote data and current season
    const month = new Date().getMonth() + 1; // 1-12
    const quoteData = quote.quote_data || {};
    
    // Check for winter services
    if (quoteData.snow_removal || quoteData.snow_program || (month >= 11 || month <= 3)) {
      return 'winter';
    }
    
    // Check for construction services
    if (quoteData.construction || quoteData.hardscaping) {
      return 'construction';
    }
    
    return 'summer';
  };

  // ====================================
  // STEP RENDERERS
  // ====================================

  const renderJobSourceStep = () => (
    <Box sx={{ minHeight: 300 }}>
      <Typography variant="h6" gutterBottom>
        How would you like to create this job?
      </Typography>
      
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper 
            sx={{ 
              p: 3, 
              cursor: 'pointer',
              border: formData.creation_method === 'from_quote' ? 2 : 1,
              borderColor: formData.creation_method === 'from_quote' ? 'primary.main' : 'divider'
            }}
            onClick={() => setFormData(prev => ({ ...prev, creation_method: 'from_quote' }))}
          >
            <Typography variant="h6" gutterBottom>From Approved Quote</Typography>
            <Typography variant="body2" color="textSecondary">
              Create a job from an existing approved quote. All customer details and services will be pre-filled.
            </Typography>
          </Paper>
        </Grid>
        
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper 
            sx={{ 
              p: 3, 
              cursor: 'pointer',
              border: formData.creation_method === 'manual' ? 2 : 1,
              borderColor: formData.creation_method === 'manual' ? 'primary.main' : 'divider'
            }}
            onClick={() => setFormData(prev => ({ ...prev, creation_method: 'manual' }))}
          >
            <Typography variant="h6" gutterBottom>Manual Job</Typography>
            <Typography variant="body2" color="textSecondary">
              Create a job manually by selecting a customer and defining the work to be done.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {formData.creation_method === 'from_quote' && (
        <Box sx={{ mt: 3 }}>
          <Autocomplete
            options={quotes}
            getOptionLabel={(option) => `#${option.quote_number} - ${option.company_name || `${option.first_name} ${option.last_name}`}`}
            value={selectedQuote}
            onChange={(event, value) => handleQuoteSelection(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Approved Quote"
                fullWidth
                required
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="subtitle2">
                    Quote #{option.quote_number}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {option.company_name || `${option.first_name} ${option.last_name}`} - ${option.total_amount}
                  </Typography>
                </Box>
              </Box>
            )}
          />
        </Box>
      )}

      {formData.creation_method === 'manual' && (
        <Box sx={{ mt: 3 }}>
          <Autocomplete
            options={customers}
            getOptionLabel={(option) => option.company_name || `${option.first_name} ${option.last_name}`}
            value={selectedCustomer}
            onChange={(event, value) => handleCustomerSelection(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Customer"
                fullWidth
                required
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <ListItemIcon>
                  {option.company_name ? <BusinessIcon /> : <PersonIcon />}
                </ListItemIcon>
                <Box>
                  <Typography variant="subtitle2">
                    {option.company_name || `${option.first_name} ${option.last_name}`}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {option.city}, {option.province}
                  </Typography>
                </Box>
              </Box>
            )}
          />
        </Box>
      )}
    </Box>
  );

  const renderDetailsStep = () => {
    const servicesByJobType = {
      summer: [
        { key: 'spring', label: 'Spring Cleanup' },
        { key: 'lawn', label: 'Lawn Care' },
        { key: 'gardening', label: 'Gardening' },
        { key: 'trimming', label: 'Trimming' },
        { key: 'turf', label: 'Turf Treatment' },
        { key: 'fall', label: 'Fall Cleanup' },
      ],
      winter: [
        { key: 'snow', label: 'Snow Services' },
      ],
      construction: [],
    };
    const servicesForThisJob = servicesByJobType[formData.job_type] || [];

    const toggleService = (key) => {
      setFormData((prev) => ({
        ...prev,
        selected_services: prev.selected_services.includes(key)
          ? prev.selected_services.filter((s) => s !== key)
          : [...prev.selected_services, key],
      }));
    };

    // TKT-238: surface whether this one-off visit will inherit the
    // customer's contracted services. The backend copies available_services
    // from the customer's most recent approved quote when the service list
    // is left blank, so the crew's clock-in/out checklist matches a regular
    // visit. We detect the approved quote from the already-loaded list.
    const customerApprovedQuote = (formData.creation_method === 'manual' && selectedCustomer)
      ? quotes
          .filter((q) => q.customer_id === selectedCustomer.id)
          .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))[0]
      : null;

    return (
      <Box sx={{ minHeight: 300 }}>
        <Typography variant="h6" gutterBottom>
          Job Details & Scheduling
        </Typography>

        {formData.creation_method === 'manual' && selectedCustomer && (
          customerApprovedQuote ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Leave the service list blank to copy this customer&apos;s services from their
              most recent approved quote (#{customerApprovedQuote.quote_number}) &mdash; the
              crew will see the same checklist as a regular visit. Anything you select below
              overrides that.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Customer has no approved quote yet &mdash; select services manually below.
            </Alert>
          )
        )}

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth required>
              <InputLabel>Job Type</InputLabel>
              <Select
                value={formData.job_type}
                label="Job Type"
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  job_type: e.target.value,
                  // Services reset when job_type changes - a "summer" service
                  // set is meaningless on a winter job and vice versa.
                  selected_services: [],
                }))}
              >
                <MenuItem value="summer">Summer (Lawn/Garden)</MenuItem>
                <MenuItem value="winter">Winter (Snow/Ice)</MenuItem>
                <MenuItem value="construction">Construction</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="date"
              label="Scheduled Date"
              value={formData.scheduled_date}
              onChange={(e) => setFormData((prev) => ({ ...prev, scheduled_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Job Description"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the work to be performed..."
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Assign Truck</InputLabel>
              <Select
                value={formData.truck_id}
                label="Assign Truck"
                onChange={(e) => setFormData((prev) => ({ ...prev, truck_id: e.target.value }))}
              >
                <MenuItem value="">No truck assigned</MenuItem>
                {trucks
                  .filter((truck) => truck.is_active !== false)
                  .map((truck) => (
                    <MenuItem key={truck.id} value={truck.id}>
                      <Box display="flex" alignItems="center">
                        <TruckIcon sx={{ mr: 1, fontSize: 16 }} />
                        {truck.name}
                      </Box>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          {servicesForThisJob.length > 0 && (
            <Grid size={12}>
              <Typography variant="subtitle2" gutterBottom>
                Services to perform
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Pick what the crew needs to do at this visit. These become the
                checkboxes on the crew's log screen.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {servicesForThisJob.map((svc) => {
                  const selected = formData.selected_services.includes(svc.key);
                  return (
                    <Chip
                      key={svc.key}
                      label={svc.label}
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() => toggleService(svc.key)}
                      clickable
                      aria-label={`${selected ? 'Remove' : 'Add'} ${svc.label}`}
                      aria-pressed={selected}
                    />
                  );
                })}
              </Box>
              {formData.selected_services.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  If left blank, the crew's log screen will have nothing to check off.
                </Typography>
              )}
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  const renderReviewStep = () => (
    <Box sx={{ minHeight: 300 }}>
      <Typography variant="h6" gutterBottom>
        Review Job Details
      </Typography>
      
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Job Information
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Creation Method:</strong> {formData.creation_method === 'from_quote' ? 'From Quote' : 'Manual'}
            </Typography>
            {selectedQuote && (
              <Typography variant="body2" gutterBottom>
                <strong>Quote:</strong> #{selectedQuote.quote_number}
              </Typography>
            )}
            <Typography variant="body2" gutterBottom>
              <strong>Type:</strong> <Chip label={formData.job_type} color={getJobTypeColor(formData.job_type)} size="small" />
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Scheduled:</strong> {formatScheduledDate(formData.scheduled_date)}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Description:</strong> {formData.description}
            </Typography>
            {formData.creation_method === 'manual' && formData.selected_services.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Services:</strong>{' '}
                <Box component="span" sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.5, ml: 0.5 }}>
                  {formData.selected_services.map((key) => (
                    <Chip
                      key={key}
                      label={key.charAt(0).toUpperCase() + key.slice(1)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Typography>
            )}
          </Paper>
        </Grid>
        
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Customer & Assignment
            </Typography>
            {(selectedCustomer || selectedQuote) && (
              <>
                <Typography variant="body2" gutterBottom>
                  <strong>Customer:</strong> {
                    selectedQuote 
                      ? (selectedQuote.company_name || `${selectedQuote.first_name} ${selectedQuote.last_name}`)
                      : (selectedCustomer.company_name || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`)
                  }
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Address:</strong> {
                    selectedQuote 
                      ? `${selectedQuote.city}, ${selectedQuote.province}`
                      : `${selectedCustomer.city}, ${selectedCustomer.province}`
                  }
                </Typography>
              </>
            )}
            <Typography variant="body2" gutterBottom>
              <strong>Truck:</strong> {trucks.find(t => t.id === formData.truck_id)?.name || 'Not assigned'}
            </Typography>
            {(() => {
              const tr = trucks.find(t => t.id === formData.truck_id);
              const driver = tr && (tr.driver_first_name || tr.driver_last_name)
                ? `${tr.driver_first_name || ''} ${tr.driver_last_name || ''}`.trim()
                : null;
              const passenger = tr && (tr.passenger_first_name || tr.passenger_last_name)
                ? `${tr.passenger_first_name || ''} ${tr.passenger_last_name || ''}`.trim()
                : null;
              return (
                <Typography variant="body2">
                  <strong>Crew:</strong>{' '}
                  {tr
                    ? [driver, passenger].filter(Boolean).join(' · ') || '(no crew on truck)'
                    : 'No truck assigned'}
                </Typography>
              );
            })()}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const getJobTypeColor = (type) => {
    switch (type) {
      case 'summer': return 'primary';
      case 'winter': return 'info';
      case 'construction': return 'warning';
      default: return 'default';
    }
  };

  // ====================================
  // MAIN RENDER
  // ====================================

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          minHeight: '600px'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <TaskIcon sx={{ mr: 1 }} />
          Schedule a One-Off Visit
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Use this for emergency / special visits that aren't part of a weekly route.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          For <strong>recurring weekly work</strong> (e.g., "Mrs. Smith every Tuesday"), use{' '}
          <strong>Route Planning</strong> instead &mdash; jobs are generated automatically
          each week. Only use this dialog for <strong>one-off</strong> visits.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && renderJobSourceStep()}
        {activeStep === 1 && renderDetailsStep()}
        {activeStep === 2 && renderReviewStep()}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        
        {activeStep > 0 && (
          <Button onClick={handleBack}>
            Back
          </Button>
        )}
        
        {activeStep < steps.length - 1 ? (
          <Button 
            onClick={handleNext}
            variant="contained"
            disabled={!validateStep(activeStep)}
          >
            Next
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !validateStep(activeStep)}
          >
            {loading ? 'Creating...' : 'Create Job'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateJobDialog;
